import type { TFunction } from 'i18next';

/**
 * CV spine B05: the API refuses a skill undo that would overwrite work done
 * after the run (409 SKILL_UNDO_CONFLICT, nothing written). `meta` names the
 * parts the undo would replace and whether "Restore anyway" (`force: true`)
 * is allowed; it never is for a copy that gained edits.
 */
export const SKILL_UNDO_CONFLICT = 'SKILL_UNDO_CONFLICT';

/** The field names the API uses (apps/api skill-run-undo.ts SKILL_UNDO_FIELDS). */
export const SKILL_UNDO_FIELD_KEYS = [
  'title',
  'summary',
  'headline',
  'contact',
  'skills',
  'experience',
  'education',
  'certifications',
  'highlights',
  'projects',
  'languages',
  'design',
  'layout',
  'content',
  'other',
] as const;

export interface SkillUndoConflict {
  fields: string[];
  canRestoreAnyway: boolean;
  /** 'edited' | 'unknown_base' | 'copy_edited' | 'no_pre_image' */
  reason: string;
}

/** The conflict a failed undo carries, or null for any other failure. */
export function readSkillUndoConflict(error: unknown): SkillUndoConflict | null {
  if (!error || typeof error !== 'object') return null;
  const { code, meta } = error as { code?: unknown; meta?: unknown };
  if (code !== SKILL_UNDO_CONFLICT) return null;
  const details = (meta && typeof meta === 'object' ? meta : {}) as Record<
    string,
    unknown
  >;
  return {
    fields: Array.isArray(details.fields)
      ? details.fields.filter((field): field is string => typeof field === 'string')
      : [],
    canRestoreAnyway: details.canRestoreAnyway === true,
    reason: typeof details.reason === 'string' ? details.reason : 'edited',
  };
}

const KNOWN = new Set<string>(SKILL_UNDO_FIELD_KEYS);

/**
 * The fields as one list in the user's language ("Summary, Experience").
 * Unknown names read as "Other details"; an empty list as "CV content".
 */
export function formatSkillUndoFields(fields: string[], t: TFunction): string {
  const keys = [...new Set(fields.map((field) => (KNOWN.has(field) ? field : 'other')))];
  const labels = (keys.length > 0 ? keys : ['content']).map((key) =>
    t(`skills.undoConflict.fields.${key}`),
  );
  return labels.join(t('skills.undoConflict.separator'));
}
